import {
    CheckIcon,
    CheckCircleIcon,
    AttachmentIcon,
    CloseIcon,
    DownloadIcon,
} from '@chakra-ui/icons';
import {
    AlertTitle,
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Flex,
    HStack,
    ListItem,
    Spinner,
    UnorderedList,
    useDisclosure,
} from '@chakra-ui/react';
import Papa from 'papaparse';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { LuCheckCircle, LuX } from 'react-icons/lu';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useRegistrarsMini } from '@/pages/resources/aors/queries/useRegistrarsMini';
import { trsRegistrationApi } from '@/services/trsRegistrationApi';

import {
    Alert,
    AlertDescription,
    AlertIcon,
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    CustomSelect,
    LoadingIndicator,
    Option,
    Text,
} from '@/components/v4';
import { Error as ErrorComponent } from 'components/index';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import { EnabledCellRenderer } from 'pages/resources/aors/Index';
import FeatureDisabledPage from 'pages/resources/feature-disabled-page';
import DeleteConfirmationModal from './DeleteConfirmationModal';

import appConfig from '@/config.json';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import { breadCrumbTrailCsv as breadCrumbTrail } from 'pages/resources/aors/constants';

const csvChakraCardStyles = {
    '& .chakra-card__header, & .chakra-card__body, & .chakra-card__footer': {
        p: 0,
    },
};

const actionColors = {
    New: 'bg-green-100 text-green-600',
    Created: 'bg-green-100 text-green-600',
    Update: 'bg-orange-100 text-orange-700',
    Updated: 'bg-orange-100 text-orange-700',
    Delete: 'bg-red-100 text-red-700',
    Deleted: 'bg-red-100 text-red-700',
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// TRS Registration verification steps
const TRS_VERIFICATION_CHECKS = [
    {
        label: 'Received CSV',
        check: false,
        current: true,
    },
    {
        label: 'Fetching Registrar Data',
        check: false,
        current: false,
    },
    {
        label: 'Parsing CSV File',
        check: false,
        current: false,
    },
    {
        label: 'Validating Extensions',
        check: false,
        current: false,
    },
    {
        label: 'Checking for Duplicates',
        check: false,
        current: false,
    },
    {
        label: 'Comparing with Existing Data',
        check: false,
        current: false,
    },
];

// Import progress steps
const TRS_IMPORT_CHECKS = [
    {
        label: 'Preparing Import',
        check: false,
        current: true,
    },
    {
        label: 'Processing Records',
        check: false,
        current: false,
    },
    {
        label: 'Finalizing Changes',
        check: false,
        current: false,
    },
];

// Reusable Components
const PageWrapper = ({ children }) => (
    <div className="min-h-screen">{children}</div>
);

const BreadcrumbNav = ({
    breadCrumbTrail,
    defaultLocationSearch,
    selectedRegistrar,
}) => (
    <Breadcrumb sx={breadCrumbStyling}>
        {breadCrumbTrail?.map((crumb, index) => (
            <BreadcrumbItem key={index}>
                {Array.isArray(crumb.path) ? (
                    <BreadcrumbLink
                        as={Link}
                        to={crumb.path[0]}
                        state={crumb.path[1]}>
                        {crumb.label}
                    </BreadcrumbLink>
                ) : (
                    <BreadcrumbLink
                        as={Link}
                        to={`${crumb.path}${defaultLocationSearch}`}
                        state={{
                            registrar: selectedRegistrar,
                            index: 0,
                        }}>
                        {crumb.label}
                    </BreadcrumbLink>
                )}
            </BreadcrumbItem>
        ))}
    </Breadcrumb>
);

const VerifyStep = ({ label, check }) => (
    <Box display="flex" alignItems="center" gap={3} py={1}>
        {check ? (
            <Box
                w={5}
                h={5}
                borderRadius="full"
                bg="green.500"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}>
                <CheckIcon color="white" boxSize={2.5} />
            </Box>
        ) : (
            <Spinner size="sm" color="blue.500" flexShrink={0} />
        )}
        <Text
            fontSize="sm"
            color={check ? 'green.600' : 'gray.700'}
            fontWeight={check ? 'normal' : 'medium'}>
            {label}
        </Text>
    </Box>
);

const VerifyCSVView = ({
    verificationChecks,
    errors,
    onComplete,
    onStartOver,
    darkMode,
    title = 'Validation status',
}) => {
    const allComplete = verificationChecks.every((v) => v.check);

    return (
        <Box display="flex" justifyContent="start" width="100%">
            <Box
                width="100%"
                maxWidth="700px"
                display="flex"
                flexDirection="column"
                gap="0"
                fontFamily="Inter"
                boxShadow="base"
                p="8"
                bg={darkMode ? 'dark.tcap' : 'white'}
                rounded="md">
                <Text fontSize="2xl" fontWeight="semibold" mb={4}>
                    {title}
                </Text>
                {errors ? (
                    <>
                        <Alert status="error" mb={4}>
                            <AlertIcon />
                            <Text size="sm" whiteSpace="pre-line">
                                {errors}
                            </Text>
                        </Alert>
                        <Box display="flex" justifyContent="left" mt={2}>
                            <Button size="sm" minW="150" onClick={onStartOver}>
                                Start Over
                            </Button>
                        </Box>
                    </>
                ) : (
                    <>
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={1}
                            mt={2}
                            p={4}
                            bg={darkMode ? 'gray.700' : 'gray.50'}
                            borderRadius="md"
                            border="1px solid"
                            borderColor={darkMode ? 'gray.600' : 'gray.200'}>
                            {verificationChecks.map((v, i) => (
                                <VerifyStep
                                    key={i}
                                    label={v.label}
                                    check={v.check}
                                />
                            ))}
                        </Box>

                        {allComplete && (
                            <HStack
                                justifyContent="flex-end"
                                spacing={3}
                                mt={5}>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    minW="150"
                                    onClick={onStartOver}>
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    minW="150"
                                    onClick={onComplete}>
                                    View Results
                                </Button>
                            </HStack>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default function CSVUpload() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedRegistrar, setSelectedRegistrar] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [step, setStep] = useState('upload');
    const [validRows, setValidRows] = useState([]);
    const [invalidRows, setInvalidRows] = useState([]);
    const [importResults, setImportResults] = useState([]);
    const [showNoChangeDialog, setShowNoChangeDialog] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [csvRowCount, setCsvRowCount] = useState(null);
    const [verificationChecks, setVerificationChecks] = useState([]);
    const [importChecks, setImportChecks] = useState([]);
    const [validationErrors, setValidationErrors] = useState('');
    const navigate = useNavigate();

    // Grid refs for AG Grid tables
    const failedImportsGridRef = useRef();
    const successfulImportsGridRef = useRef();
    const validDataGridRef = useRef();
    const invalidDataGridRef = useRef();

    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { restError } = useSelector((state) => state.modal);
    const { darkMode } = useSelector((state) => state.settings);
    const isNotEnabled = restError?.includes('not enabled') || false;
    const registrarsError = error?.includes('registrars') || false;

    // Delete confirmation modal state
    const {
        isOpen: isOpenDeleteModal,
        onOpen: onOpenDeleteModal,
        onClose: onCloseDeleteModal,
    } = useDisclosure();

    const {
        data: registrars = [],
        isLoading: loadingRegistrars,
        error: registrarsQueryError,
    } = useRegistrarsMini(currentCompany);

    const currentRegistrar = registrars?.find(
        (r) => r.id === selectedRegistrar,
    );
    const currentRegistrarName = currentRegistrar?.name
        .split(' ')
        .join('_')
        ?.toLowerCase();
    const defaultLocationSearch = selectedRegistrar
        ? `?registrar=${selectedRegistrar}`
        : '';

    // Handle side effects when registrars data changes
    useEffect(() => {
        if (registrars && registrars.length > 0 && !selectedRegistrar) {
            const initRegistrarId = new URLSearchParams(searchParams).get(
                'registrar',
            );
            if (initRegistrarId) {
                setSelectedRegistrar(initRegistrarId);
            } else {
                setSelectedRegistrar(registrars[0].id);
            }
        }
    }, [registrars, searchParams, selectedRegistrar]);

    // Handle query error
    useEffect(() => {
        if (registrarsQueryError) {
            console.error('Error loading registrars:', registrarsQueryError);
            setError('Failed to load registrars');
        }
    }, [registrarsQueryError]);

    // Column definitions for AG Grid tables
    const failedImportsColumns = useMemo(
        () => [
            {
                headerName: 'Extension',
                field: 'extension',
                flex: 1,
                minWidth: 120,
            },
            {
                headerName: 'Action',
                field: 'action',
                flex: 1,
                minWidth: 100,
                cellRenderer: (params) => {
                    const colorClass =
                        actionColors[params.value] ||
                        'bg-gray-100 text-gray-700';
                    return (
                        <span
                            className={`inline-block min-w-14 rounded px-2 py-1 text-xs font-medium ${colorClass}`}>
                            {params.value}
                        </span>
                    );
                },
            },
            {
                headerName: 'Error',
                field: 'error',
                flex: 2,
                minWidth: 200,
            },
        ],
        [],
    );

    const successfulImportsColumns = useMemo(
        () => [
            {
                headerName: 'Extension',
                field: 'extension',
                flex: 1,
                minWidth: 120,
            },
            {
                headerName: 'Action',
                field: 'action',
                flex: 1,
                minWidth: 100,
                cellRenderer: (params) => {
                    const colorClass =
                        actionColors[params.value] ||
                        'bg-gray-100 text-gray-700';
                    return (
                        <span
                            className={`inline-block min-w-14 rounded px-2 py-1 text-xs font-medium ${colorClass}`}>
                            {params.value}
                        </span>
                    );
                },
            },
            {
                headerName: 'Status',
                field: 'success',
                flex: 1,
                minWidth: 100,
                cellRenderer: () => (
                    <span className="inline-flex items-center text-green-600">
                        <LuCheckCircle className="mr-1 h-4 w-4" />
                        Success
                    </span>
                ),
            },
        ],
        [],
    );

    const validDataColumns = useMemo(
        () => [
            {
                headerName: 'Extension',
                field: 'Extension',
                flex: 1,
                minWidth: 120,
            },
            {
                headerName: 'Username',
                field: 'Username',
                flex: 1,
                minWidth: 120,
                valueGetter: (params) => params.data['Username'],
            },
            {
                headerName: 'Active',
                field: '_normalized.isEnabled',
                flex: 1,
                minWidth: 100,
                cellRenderer: (params) =>
                    EnabledCellRenderer({
                        value: params.data._normalized?.isEnabled,
                    }),
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }),
                valueFormatter: (params) => {
                    return params.data._normalized?.isEnabled
                        ? 'Enabled'
                        : 'Disabled';
                },
            },
            {
                headerName: 'Authentication',
                field: '_normalized.overrideCredentials',
                flex: 1,
                minWidth: 130,
                cellRenderer: (params) =>
                    EnabledCellRenderer({
                        value: params.data._normalized?.overrideCredentials,
                    }),
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }),
                valueFormatter: (params) => {
                    return params.data._normalized?.overrideCredentials
                        ? 'Enabled'
                        : 'Disabled';
                },
            },
            {
                headerName: 'Advanced Transfer',
                field: '_normalized.useAdvancedTransfer',
                flex: 1,
                minWidth: 140,
                cellRenderer: (params) =>
                    EnabledCellRenderer({
                        value: params.data._normalized?.useAdvancedTransfer,
                    }),
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }),
                valueFormatter: (params) => {
                    return params.data._normalized?.useAdvancedTransfer
                        ? 'Enabled'
                        : 'Disabled';
                },
            },
            {
                headerName: 'Action',
                field: '_changeType',
                flex: 1,
                minWidth: 100,
                cellRenderer: (params) => {
                    const colorClass =
                        actionColors[params.value] ||
                        'bg-gray-100 text-gray-700';
                    return (
                        <span
                            className={`inline-block min-w-14 rounded px-2 py-1 text-xs font-medium ${colorClass}`}>
                            {params.value}
                        </span>
                    );
                },
            },
        ],
        [],
    );

    const invalidDataColumns = useMemo(
        () => [
            {
                headerName: 'Registrar ID',
                field: 'registrarID',
                flex: 1,
                minWidth: 120,
                valueGetter: (params) =>
                    params.data._normalized?.['registrarID'],
            },
            {
                headerName: 'Registration',
                field: 'id',
                flex: 1,
                minWidth: 120,
                valueGetter: (params) => params.data._normalized?.['id'],
            },
            {
                headerName: 'Extension',
                field: 'Extension',
                flex: 1,
                minWidth: 120,
            },
            {
                headerName: 'Username',
                field: 'Username',
                flex: 1,
                minWidth: 120,
                valueGetter: (params) => params.data['Username'],
            },
            {
                headerName: 'Errors',
                field: '_errors',
                flex: 2,
                minWidth: 200,
                valueGetter: (params) => params.data._errors?.join(', ') || '',
            },
        ],
        [],
    );

    // Helper function to update verification check progress
    const updateVerificationCheck = (index, updates) => {
        setVerificationChecks((prev) => {
            const newChecks = [...prev];
            newChecks[index] = { ...newChecks[index], ...updates };
            return newChecks;
        });
    };

    // Helper function to update import check progress
    const updateImportCheck = (index, updates) => {
        setImportChecks((prev) => {
            const newChecks = [...prev];
            newChecks[index] = { ...newChecks[index], ...updates };
            return newChecks;
        });
    };

    // Simulate async delay for visual feedback
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    // Validation with progress visualization
    const validateWithProgress = async (file) => {
        try {
            // Step 1: Received CSV
            updateVerificationCheck(0, { current: true });
            await delay(400);
            updateVerificationCheck(0, { check: true, current: false });

            // Step 2: Fetching Registrar Data
            updateVerificationCheck(1, { current: true });
            const existingData =
                await trsRegistrationApi.getRegistrationsByRegistrar(
                    selectedRegistrar,
                );
            await delay(300);
            updateVerificationCheck(1, { check: true, current: false });

            // Step 3: Parsing CSV File
            updateVerificationCheck(2, { current: true });
            const text = await file.text();
            const rows = parseCSV(text);
            if (rows.length === 0) {
                throw new Error('No data found in CSV file');
            }
            await delay(300);
            updateVerificationCheck(2, { check: true, current: false });

            // Step 4: Validating Extensions
            updateVerificationCheck(3, { current: true });
            await delay(400);
            updateVerificationCheck(3, { check: true, current: false });

            // Step 5: Checking for Duplicates
            updateVerificationCheck(4, { current: true });
            await delay(300);
            updateVerificationCheck(4, { check: true, current: false });

            // Step 6: Comparing with Existing Data
            updateVerificationCheck(5, { current: true });

            // Create maps for both extension-based and id-based lookup
            const existingByExtension = new Map();
            const existingById = new Map();
            existingData?.forEach((item) => {
                existingByExtension.set(item.extension, item);
                if (item.id) {
                    existingById.set(item.id, item);
                }
            });

            // Track extensions to detect duplicates
            const extensionsSeen = new Set();

            const parsed = rows.map((row, index) => {
                const errors = [];

                // Validate RegistrarID (required)
                if (
                    !row['RegistrarID'] ||
                    row['RegistrarID'] !== selectedRegistrar
                ) {
                    errors.push('Invalid or mismatched RegistrarID');
                }

                // Validate Extension (required, minimum 3 characters)
                if (!row['Extension'] || row['Extension'].length < 3) {
                    errors.push('Extension must be at least 3 characters');
                }

                // Check for duplicate extensions in CSV
                if (row['Extension']) {
                    if (extensionsSeen.has(row['Extension'])) {
                        errors.push('Duplicate extension in CSV');
                    }
                    extensionsSeen.add(row['Extension']);
                }

                // Normalize and validate Enabled field
                const enabledValue = (
                    row['Enabled']?.toLowerCase() || 'true'
                ).trim();
                if (!['true', 'false'].includes(enabledValue)) {
                    errors.push(
                        'Enabled must be true or false (empty defaults to true)',
                    );
                }
                const isEnabled = enabledValue === 'true';

                // Normalize and validate Override Credentials field
                const overrideValue = (
                    row['Override Credentials']?.toLowerCase() || ''
                ).trim();
                if (
                    overrideValue !== '' &&
                    !['true', 'false'].includes(overrideValue)
                ) {
                    errors.push(
                        'Override Credentials must be true or false (empty for auto-detect)',
                    );
                }

                // Determine override credentials with improved logic
                let overrideCredentials = false;
                let autoDetected = false;

                const hasCredentials = !!(
                    row['Username']?.trim() && row['Password']?.trim()
                );

                if (hasCredentials) {
                    // If credentials are provided, override MUST be true
                    overrideCredentials = true;

                    if (overrideValue === 'false') {
                        // User specified false but provided credentials - auto-correct
                        autoDetected = true;
                    } else if (
                        overrideValue === '' ||
                        overrideValue === 'true'
                    ) {
                        // Mark as auto-detected only if the field was empty
                        autoDetected = overrideValue === '';
                    }
                } else {
                    // No credentials provided
                    if (overrideValue === 'true') {
                        // User specified true but didn't provide credentials - error
                        errors.push(
                            'Username and Password required when Override Credentials is true',
                        );
                    }
                    overrideCredentials = false;
                }

                // Normalize and validate Advanced Transfer field
                const advancedTransferValue = (
                    row['Advanced Transfer']?.toLowerCase() || 'false'
                ).trim();
                if (!['true', 'false'].includes(advancedTransferValue)) {
                    errors.push(
                        'Advanced Transfer must be true or false (empty defaults to false)',
                    );
                }
                const useAdvancedTransfer = advancedTransferValue === 'true';

                // Validate Delete field
                const deleteValue = (
                    row['Delete']?.toLowerCase() || 'false'
                ).trim();
                if (!['true', 'false'].includes(deleteValue)) {
                    errors.push(
                        'Delete must be true or false (empty defaults to false)',
                    );
                }
                const isDelete = deleteValue === 'true';

                // Find existing record
                let existing = null;
                if (row['Registration ID'] && row['Registration ID'].trim()) {
                    existing = existingById.get(row['Registration ID']);
                    if (!existing) {
                        errors.push('Registration ID not found in system');
                    }
                }

                // Validate immutable fields for existing records
                if (existing) {
                    // Check if registrar is being changed
                    if (row['RegistrarID'] !== existing.registrarID) {
                        errors.push(
                            'Cannot change Registrar for existing registration',
                        );
                    }

                    // Check if extension is being changed
                    if (row['Extension'] !== existing.extension) {
                        errors.push(
                            'Cannot change Extension for existing registration',
                        );
                    }
                }

                const isNew = !existing;

                // Detect changes for existing records
                let hasChanges = false;
                let changeType = 'None';

                if (isDelete) {
                    changeType = 'Delete';
                    hasChanges = true;
                } else if (isNew) {
                    changeType = 'New';
                    hasChanges = true;
                } else if (existing) {
                    // Compare fields to detect changes
                    const username = overrideCredentials
                        ? row['Username']?.trim() || ''
                        : '';
                    const password = overrideCredentials
                        ? row['Password']?.trim() || ''
                        : '';

                    const existingUsername = existing.username || '';
                    const existingPassword = existing.password || '';
                    const existingOverride = !!(
                        existingUsername && existingPassword
                    );

                    const existingAdvancedTransfer =
                        existing.useAdvancedTransfer || false;

                    hasChanges =
                        existing.isEnabled !== isEnabled ||
                        existingUsername !== username ||
                        existingPassword !== password ||
                        existingOverride !== overrideCredentials ||
                        existingAdvancedTransfer !== useAdvancedTransfer;

                    if (hasChanges) {
                        changeType = 'Update';
                    }
                }

                // Apply defaults for optional fields
                const username = overrideCredentials
                    ? row['Username']?.trim() || ''
                    : '';
                const password = overrideCredentials
                    ? row['Password']?.trim() || ''
                    : '';

                return {
                    ...row,
                    _index: index,
                    _errors: errors,
                    _isValid: errors.length === 0,
                    _isNew: isNew,
                    _isDelete: isDelete,
                    _hasChanges: hasChanges,
                    _changeType: changeType,
                    _existingRecord: existing,
                    _autoDetectedOverride: autoDetected,
                    // Normalized values for import
                    _normalized: {
                        id:
                            row['Registration ID']?.trim() ||
                            existing?.id ||
                            null,
                        registrarID: row['RegistrarID'],
                        extension: row['Extension'],
                        username,
                        password,
                        isEnabled,
                        overrideCredentials,
                        useAdvancedTransfer,
                        delete: isDelete,
                    },
                };
            });

            await delay(500);
            updateVerificationCheck(5, { check: true, current: false });

            const valid = parsed.filter((r) => r._isValid);
            const invalid = parsed.filter((r) => !r._isValid);

            // Filter out records with no changes (unless they're deletions)
            const validWithChanges = valid.filter((r) => r._hasChanges);

            if (validWithChanges.length === 0 && invalid.length === 0) {
                setShowNoChangeDialog(true);
                setStep('upload');
                return;
            }

            setValidRows(validWithChanges);
            setInvalidRows(invalid);
        } catch (err) {
            setValidationErrors(
                err instanceof Error ? err.message : 'Failed to validate CSV',
            );
        }
    };

    const downloadTemplate = async () => {
        if (!selectedRegistrar) {
            setError('Please select a registrar first');
            return;
        }

        setLoading(true);
        try {
            const existingData =
                await trsRegistrationApi.getRegistrationsByRegistrar(
                    selectedRegistrar,
                );

            // CSV Header with all fields
            let csvContent =
                'RegistrarID,Registration ID,Extension,Username,Password,Enabled,Override Credentials,Advanced Transfer,Delete\n';

            if (existingData && existingData.length > 0) {
                // Export existing data
                existingData.forEach((row) => {
                    const id = row.id || '';
                    const registrarID = row.registrarID || selectedRegistrar;
                    const extension = row.extension || '';
                    const username = row.username || '';
                    const password = row.password || '';
                    const enabled = row.isEnabled ? 'true' : 'false';
                    const overrideCredentials =
                        row.username && row.password ? 'true' : 'false';
                    const advancedTransfer = row.useAdvancedTransfer
                        ? 'true'
                        : 'false';
                    const deleteFlag = 'false';

                    csvContent += `${registrarID},${id},${extension},${username},${password},${enabled},${overrideCredentials},${advancedTransfer},${deleteFlag}\n`;
                });
            }
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `trs_registrations_${currentRegistrarName}_${Date.now()}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to download template',
            );
        } finally {
            setLoading(false);
        }
    };

    const parseCSV = (text) => {
        // Use PapaParse for proper CSV parsing (handles quoted values, commas, etc.)
        const result = Papa.parse(text, {
            header: true,
            skipEmptyLines: true,
            comments: '#',
            dynamicTyping: false, // Keep as strings for validation
            transformHeader: (header) => header.trim(),
            transform: (value) => value.trim(),
        });

        if (result.errors.length > 0) {
            console.warn('CSV parsing warnings:', result.errors);
        }

        return result.data;
    };

    const processFile = async (file) => {
        if (!file) return;

        // Validate file type
        if (
            !file.name.toLowerCase().endsWith('.csv') ||
            (file.type !== 'text/csv' &&
                file.type !== 'application/vnd.ms-excel') // Firefox Windows case
        ) {
            setError('Please upload a CSV file');
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            setError('File size exceeds the limit of 10MB');
            return;
        }

        setError('');
        setValidationErrors('');

        // Initialize verification checks and start validation with progress
        setVerificationChecks(
            JSON.parse(JSON.stringify(TRS_VERIFICATION_CHECKS)),
        );
        setStep('validating');

        await validateWithProgress(file);
    };

    // Handle validate button click
    const handleValidate = () => {
        if (uploadedFile) {
            processFile(uploadedFile);
        }
    };

    // Handle start over from validation error
    const handleStartOver = () => {
        setStep('upload');
        setUploadedFile(null);
        setCsvRowCount(null);
        setValidRows([]);
        setInvalidRows([]);
        setError('');
        setValidationErrors('');
        setVerificationChecks([]);
    };

    // Dropzone callback for handling dropped/selected files
    const onDrop = async (acceptedFiles) => {
        if (loading || !selectedRegistrar) {
            return;
        }

        if (acceptedFiles && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            setUploadedFile(file);
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    setCsvRowCount(results.data.length);
                },
            });
            await processFile(file);
        }
    };

    const handleRemoveFile = (e) => {
        e.stopPropagation();
        setUploadedFile(null);
        setCsvRowCount(null);
    };

    // Configure react-dropzone hook
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'text/csv': ['.csv'] },
        multiple: false,
        disabled: loading || !selectedRegistrar,
    });

    const handleImport = async () => {
        // Check if there are any deletions and confirm
        const deleteCount = validRows.filter((r) => r._isDelete).length;
        if (deleteCount > 0) {
            // Open the delete confirmation modal
            onOpenDeleteModal();
        } else {
            // No deletions, proceed with import
            await performImport();
        }
    };

    const handleConfirmDelete = async () => {
        // User confirmed the deletion, proceed with import
        await performImport();
    };

    const performImport = async () => {
        // Initialize import checks and start importing
        setImportChecks(JSON.parse(JSON.stringify(TRS_IMPORT_CHECKS)));
        setStep('importing');
        const results = [];

        try {
            // Step 1: Preparing Import
            updateImportCheck(0, { current: true });
            await delay(300);
            updateImportCheck(0, { check: true, current: false });

            // Step 2: Processing Records
            updateImportCheck(1, { current: true });

            for (let i = 0; i < validRows.length; i++) {
                const row = validRows[i];
                const normalized = row._normalized;

                try {
                    if (row._isDelete) {
                        // Handle deletion using DELETE method
                        if (!normalized.id) {
                            throw new Error(
                                'Registration ID is required for deletion',
                            );
                        }

                        await trsRegistrationApi.deleteRegistration(
                            normalized.id,
                        );

                        results.push({
                            success: true,
                            extension: normalized.extension,
                            action: 'Deleted',
                        });
                    } else if (row._isNew) {
                        await trsRegistrationApi.createRegistration({
                            registrarID: normalized.registrarID,
                            extension: normalized.extension,
                            username: normalized.username,
                            password: normalized.password,
                            overrideCredentials: normalized.overrideCredentials,
                            isEnabled: normalized.isEnabled,
                            useAdvancedTransfer: normalized.useAdvancedTransfer,
                            companyID: currentCompany,
                        });

                        results.push({
                            success: true,
                            extension: normalized.extension,
                            action: 'Created',
                        });
                    } else if (row._hasChanges) {
                        await trsRegistrationApi.updateRegistration({
                            id: normalized.id,
                            registrarID: normalized.registrarID,
                            extension: normalized.extension,
                            username: normalized.username,
                            password: normalized.password,
                            overrideCredentials: normalized.overrideCredentials,
                            isEnabled: normalized.isEnabled,
                            useAdvancedTransfer: normalized.useAdvancedTransfer,
                            delete: false,
                            companyID: currentCompany,
                        });

                        results.push({
                            success: true,
                            extension: normalized.extension,
                            action: 'Updated',
                        });
                    }
                } catch (err) {
                    results.push({
                        success: false,
                        extension: normalized.extension,
                        action: row._changeType,
                        error: getErrorMessage(err),
                    });
                }
            }

            updateImportCheck(1, { check: true, current: false });

            // Step 3: Finalizing Changes
            updateImportCheck(2, { current: true });
            await delay(400);
            updateImportCheck(2, { check: true, current: false });

            setImportResults(results);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to import data',
            );
            setStep('validate'); // Go back to validation screen on error
        }
    };

    const getErrorMessage = (error) => {
        // First priority: Backend's specific error message
        if (error?.response?.data?.message) {
            return error.response.data.message;
        }

        // Second priority: Backend's title (alternative format)
        if (error?.response?.data?.title) {
            return error.response.data.title;
        }

        // Fallback
        if (error?.response?.status === 409) {
            return 'Extension already exists for another registrar';
        }
        if (error?.response?.status === 404) {
            return 'Registration ID not found';
        }
        if (error?.response?.status === 400) {
            return 'Invalid data provided';
        }
        if (error?.response?.status === 500) {
            return 'Server error occurred. Please try again later.';
        }

        // Error instance message
        if (error instanceof Error && error.message) {
            return error.message;
        }

        // Generic error
        return 'An unexpected error occurred';
    };

    const handleCancel = () => {
        setStep('upload');
        setValidRows([]);
        setInvalidRows([]);
        setError('');
        setImportResults([]);
        setUploadedFile(null);
        setCsvRowCount(null);
    };

    const exportInvalidData = () => {
        let csvContent =
            'RegistrarID,Registration ID,Extension,Username,Password,Enabled,Override Credentials,Delete,Errors\n';
        invalidRows.forEach((row) => {
            csvContent += `${row['RegistrarID']},${row['Registration ID'] || ''},${row['Extension']},${row['Username'] || ''},${row['Password'] || ''},${row['Enabled']},${row['Override Credentials'] || ''},${row['Delete'] || '0'},"${row._errors.join('; ')}"\n`;
        });

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invalid_data_${currentRegistrarName}_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    if (showNoChangeDialog) {
        return (
            <PageWrapper>
                <BreadcrumbNav
                    breadCrumbTrail={breadCrumbTrail}
                    defaultLocationSearch={defaultLocationSearch}
                    selectedRegistrar={selectedRegistrar}
                />
                <Card
                    sx={csvChakraCardStyles}
                    bg={darkMode ? 'dark.tcap' : null}
                    p={8}
                    className="mr-auto max-w-3xl">
                    <CardHeader
                        as="h1"
                        mb={4}
                        className="text-2xl font-semibold">
                        TRS Registration CSV Upload
                    </CardHeader>
                    <CardBody>
                        <Alert status="info">
                            <AlertIcon />
                            <AlertTitle>No Changes Detected</AlertTitle>
                            <AlertDescription>
                                The uploaded CSV contains no new or modified
                                data compared to the existing records.
                            </AlertDescription>
                        </Alert>
                    </CardBody>
                    <CardFooter mt={6} className="justify-end">
                        <Button
                            minW="150"
                            size="sm"
                            onClick={() => {
                                setShowNoChangeDialog(false);
                                handleCancel();
                            }}>
                            Finish
                        </Button>
                    </CardFooter>
                </Card>
            </PageWrapper>
        );
    }

    if (step === 'validating') {
        return (
            <PageWrapper>
                <BreadcrumbNav
                    breadCrumbTrail={breadCrumbTrail}
                    defaultLocationSearch={defaultLocationSearch}
                    selectedRegistrar={selectedRegistrar}
                />
                <VerifyCSVView
                    verificationChecks={verificationChecks}
                    errors={validationErrors}
                    onComplete={() => setStep('validate')}
                    onStartOver={handleStartOver}
                    darkMode={darkMode}
                />
            </PageWrapper>
        );
    }

    if (step === 'importing') {
        return (
            <PageWrapper>
                <BreadcrumbNav
                    breadCrumbTrail={breadCrumbTrail}
                    defaultLocationSearch={defaultLocationSearch}
                    selectedRegistrar={selectedRegistrar}
                />
                <VerifyCSVView
                    verificationChecks={importChecks}
                    errors=""
                    onComplete={() => setStep('complete')} // Will auto-transition to complete
                    onStartOver={handleStartOver}
                    darkMode={darkMode}
                    title="Importing Data"
                />
            </PageWrapper>
        );
    }

    if (step === 'complete') {
        const successCount = importResults.filter((r) => r.success).length;
        const failCount = importResults.filter((r) => !r.success).length;
        const noChanges = successCount === 0 && failCount === 0;

        return (
            <PageWrapper>
                <Card
                    p={8}
                    sx={csvChakraCardStyles}
                    bg={darkMode ? 'dark.tcap' : null}
                    className="mr-auto max-w-3xl">
                    <CardBody>
                        {/* Centered status header */}
                        <Flex
                            direction="column"
                            alignItems="center"
                            py={8}
                            mb={6}>
                            <CheckCircleIcon
                                boxSize={16}
                                color={noChanges ? 'blue.400' : 'green.400'}
                                mb={4}
                            />
                            <Text
                                fontSize="2xl"
                                fontWeight="semibold"
                                textAlign="center"
                                mb={2}>
                                {noChanges
                                    ? 'No changes detected'
                                    : 'Import successful'}
                            </Text>
                            <Text
                                color="gray.500"
                                fontSize="sm"
                                textAlign="center">
                                TRS Registrations processed for selected
                                registrar.
                            </Text>
                        </Flex>

                        {/* Failed imports */}
                        {failCount > 0 && (
                            <Box mb={6}>
                                <HStack justifyContent="space-between" mb={3}>
                                    <Text
                                        fontWeight="semibold"
                                        fontSize="md"
                                        color="red.600">
                                        Failed Imports ({failCount})
                                    </Text>
                                    <Button
                                        size="sm"
                                        leftIcon={<DownloadIcon />}
                                        onClick={exportInvalidData}>
                                        Export Results
                                    </Button>
                                </HStack>
                                <AGGridMain
                                    gridRef={failedImportsGridRef}
                                    tableData={{
                                        columns: failedImportsColumns,
                                        data: importResults.filter(
                                            (r) => !r.success,
                                        ),
                                    }}
                                    domLayout="autoHeight"
                                    useFixedHeight={false}
                                />
                            </Box>
                        )}

                        {successCount > 0 && (
                            <Box mb={6}>
                                <HStack justifyContent="space-between" mb={3}>
                                    <Text fontWeight="semibold" fontSize="md">
                                        Summary of changes
                                    </Text>
                                    <Button
                                        size="sm"
                                        leftIcon={<DownloadIcon />}
                                        onClick={exportInvalidData}>
                                        Export Results
                                    </Button>
                                </HStack>
                                <AGGridMain
                                    tableData={{
                                        columns: successfulImportsColumns,
                                        data: importResults.filter(
                                            (r) => r.success,
                                        ),
                                    }}
                                    gridRef={successfulImportsGridRef}
                                    domLayout="autoHeight"
                                    useFixedHeight={false}
                                />
                            </Box>
                        )}
                    </CardBody>

                    <CardFooter className="justify-end">
                        <Button
                            size="sm"
                            minW="150"
                            variant="outline"
                            onClick={handleCancel}>
                            Upload another file
                        </Button>
                    </CardFooter>
                </Card>
            </PageWrapper>
        );
    }

    if (step === 'validate') {
        const deleteCount = validRows.filter((r) => r._isDelete).length;

        return (
            <PageWrapper>
                <BreadcrumbNav
                    breadCrumbTrail={breadCrumbTrail}
                    defaultLocationSearch={defaultLocationSearch}
                    selectedRegistrar={selectedRegistrar}
                />
                <Card
                    sx={csvChakraCardStyles}
                    bg={darkMode ? 'dark.tcap' : null}
                    p={8}
                    className="mr-auto max-w-6xl">
                    <CardHeader
                        as="h1"
                        mb={4}
                        className="text-2xl font-semibold">
                        Validation Results
                    </CardHeader>

                    <CardBody>
                        {/* Summary Statistics */}
                        <div className="mb-[16px] grid grid-cols-2 gap-4 sm:grid-cols-4">
                            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                                <div className="text-2xl font-bold text-green-600">
                                    {
                                        validRows.filter(
                                            (r) => r._changeType === 'New',
                                        ).length
                                    }
                                </div>
                                <div className="text-sm text-green-600">
                                    New
                                </div>
                            </div>
                            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                                <div className="text-2xl font-bold text-orange-700">
                                    {
                                        validRows.filter(
                                            (r) => r._changeType === 'Update',
                                        ).length
                                    }
                                </div>
                                <div className="text-sm text-orange-600">
                                    Update
                                </div>
                            </div>
                            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                                <div className="text-2xl font-bold text-red-700">
                                    {
                                        validRows.filter(
                                            (r) => r._changeType === 'Delete',
                                        ).length
                                    }
                                </div>
                                <div className="text-sm text-red-600">
                                    Delete
                                </div>
                            </div>
                            {invalidRows.length > 0 && (
                                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                                    <div className="text-2xl font-bold text-gray-700">
                                        {invalidRows.length}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        Invalid
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Auto-detection warning */}
                        {validRows.some((r) => r._autoDetectedOverride) && (
                            <Alert mb={4} status="info">
                                <AlertIcon />
                                <AlertTitle>
                                    <strong>Auto-detection enabled:</strong>{' '}
                                </AlertTitle>
                                <AlertDescription>
                                    {`Some records have "Override Credentials"
                                    automatically set to "true" because both
                                    Username and Password were provided.`}
                                </AlertDescription>
                            </Alert>
                        )}

                        {invalidRows.length > 0 && (
                            <div className="mb-6">
                                <div className="mb-3 flex items-center justify-between">
                                    <h3 className="flex items-center text-xl font-semibold text-red-600">
                                        <LuX className="mr-2 h-5 w-5" />
                                        Invalid Data ({invalidRows.length})
                                    </h3>
                                    <Button
                                        size="sm"
                                        onClick={exportInvalidData}>
                                        Export Invalid Data
                                    </Button>
                                </div>

                                <AGGridMain
                                    tableData={{
                                        columns: invalidDataColumns,
                                        data: invalidRows,
                                    }}
                                    gridRef={invalidDataGridRef}
                                    domLayout="autoHeight"
                                    useFixedHeight={false}
                                />
                            </div>
                        )}

                        {/* Deletion warning */}
                        {validRows.some((r) => r._isDelete) && (
                            <Alert status="error" className="mb-6">
                                <AlertIcon />
                                <AlertDescription>
                                    <strong>Warning:</strong>{' '}
                                    {
                                        validRows.filter((r) => r._isDelete)
                                            .length
                                    }{' '}
                                    registration(s) marked for deletion. You
                                    will be asked to confirm before proceeding.
                                </AlertDescription>
                            </Alert>
                        )}

                        {validRows.length > 0 && (
                            <div className="mb-6">
                                <div className="mb-3 flex items-center justify-between">
                                    <h3 className="flex items-center text-xl font-semibold text-green-600">
                                        <LuCheckCircle className="mr-2 h-5 w-5" />
                                        Valid Data ({validRows.length})
                                    </h3>
                                </div>

                                <AGGridMain
                                    tableData={{
                                        columns: validDataColumns,
                                        data: validRows,
                                    }}
                                    gridRef={validDataGridRef}
                                    domLayout="autoHeight"
                                    useFixedHeight={false}
                                />
                            </div>
                        )}
                    </CardBody>

                    <CardFooter className="justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={handleCancel}
                            size="sm"
                            minW="150"
                            className="px-6 py-3 font-medium transition-colors">
                            Cancel
                        </Button>
                        {validRows.length > 0 && (
                            <Button size="sm" minW="150" onClick={handleImport}>
                                Import {validRows.length} Record
                                {validRows.length !== 1 ? 's' : ''}
                            </Button>
                        )}
                    </CardFooter>
                </Card>

                <DeleteConfirmationModal
                    isOpen={isOpenDeleteModal}
                    onClose={onCloseDeleteModal}
                    onConfirm={handleConfirmDelete}
                    deleteCount={deleteCount}
                />
            </PageWrapper>
        );
    }

    if (!currentCompany) {
        return (
            <ErrorComponent
                error={{
                    name: 'No Partner / Company / Wholesaler Selected',
                    message:
                        'Please select a wholesaler and a partner / company.',
                }}
            />
        );
    }

    if (loadingRegistrars) {
        return (
            <Box id="csv-upload" className="min-h-screen">
                <Box className="mx-auto">
                    <LoadingIndicator />
                </Box>
            </Box>
        );
    }

    if (registrarsError || isNotEnabled) {
        return <FeatureDisabledPage />;
    }

    return (
        <PageWrapper>
            <BreadcrumbNav
                breadCrumbTrail={breadCrumbTrail}
                defaultLocationSearch={defaultLocationSearch}
                selectedRegistrar={selectedRegistrar}
            />
            <Card
                sx={csvChakraCardStyles}
                bg={darkMode ? 'dark.tcap' : null}
                p={8}
                className="mr-auto max-w-3xl">
                <CardHeader as="h1" mb={4} className="text-2xl font-semibold">
                    TRS Registration CSV Upload
                </CardHeader>

                <CardBody>
                    {error && (
                        <Alert status="error" className="mb-3">
                            <AlertIcon />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {/* Select Registrar */}
                    <div className="mb-6">
                        <Text
                            htmlFor="registrar-select"
                            className="block text-sm"
                            fontWeight="semibold"
                            mb={2}>
                            1. Select Registrar
                        </Text>
                        {registrars.length === 0 ? (
                            <>
                                <Alert status="info" className="w-full">
                                    <AlertIcon />
                                    <AlertDescription>
                                        No registrars found. Please add
                                        registrars first.
                                    </AlertDescription>
                                </Alert>
                            </>
                        ) : (
                            <CustomSelect
                                id="registrar-select"
                                placeholder={
                                    loadingRegistrars
                                        ? 'Loading...'
                                        : 'Select a registrar'
                                }
                                value={selectedRegistrar}
                                onChange={(value) => {
                                    setSelectedRegistrar(value);
                                    setSearchParams({ registrar: value });
                                }}
                                size="sm">
                                {registrars.map((reg) => (
                                    <Option key={reg.id} value={reg.id}>
                                        {reg.name}
                                    </Option>
                                ))}
                            </CustomSelect>
                        )}
                    </div>

                    {selectedRegistrar && (
                        <>
                            {/* Download Template Button */}
                            <Box mt="25px" mb={6}>
                                <Text fontWeight="semibold" mb={2}>
                                    2. Download CSV Template{' '}
                                </Text>
                                <Button
                                    isLoading={loading}
                                    minW="150"
                                    size="sm"
                                    onClick={downloadTemplate}>
                                    Download Template
                                </Button>
                            </Box>
                            {/* Upload CSV Dropzone */}
                            <Box mb={6}>
                                <Text fontWeight="semibold" mb={2}>
                                    3. Upload CSV to be imported
                                </Text>
                                {uploadedFile ? (
                                    <Box
                                        border="2px solid"
                                        borderColor="green.400"
                                        borderRadius="md"
                                        p={5}
                                        bg="green.50"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        mb={2}>
                                        <input {...getInputProps()} />
                                        <HStack spacing={3}>
                                            <AttachmentIcon
                                                boxSize={5}
                                                color="green.500"
                                            />
                                            <Box>
                                                <Text
                                                    color="green.700"
                                                    fontWeight="semibold"
                                                    fontSize="sm">
                                                    {uploadedFile.name}
                                                </Text>
                                                <Text
                                                    fontSize="xs"
                                                    color="green.600">
                                                    Valid CSV
                                                    {csvRowCount !== null
                                                        ? ` • ${csvRowCount} Rows`
                                                        : ''}
                                                </Text>
                                            </Box>
                                        </HStack>
                                        <Button
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="red"
                                            leftIcon={<CloseIcon boxSize={2} />}
                                            onClick={handleRemoveFile}>
                                            Remove
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box
                                        {...getRootProps()}
                                        border="2px dashed"
                                        borderColor={
                                            isDragActive
                                                ? 'blue.400'
                                                : 'gray.200'
                                        }
                                        borderRadius="md"
                                        p={8}
                                        textAlign="center"
                                        bg={
                                            isDragActive ? 'blue.50' : 'gray.50'
                                        }
                                        cursor={
                                            loading || !selectedRegistrar
                                                ? 'not-allowed'
                                                : 'pointer'
                                        }
                                        opacity={
                                            loading || !selectedRegistrar
                                                ? 0.5
                                                : 1
                                        }
                                        transition="border-color 0.2s, background 0.2s"
                                        mb={2}>
                                        <input {...getInputProps()} />
                                        <AttachmentIcon
                                            boxSize={10}
                                            color={
                                                isDragActive
                                                    ? 'blue.400'
                                                    : 'gray.400'
                                            }
                                            mb={2}
                                        />
                                        <Text
                                            color="blue.600"
                                            fontWeight="bold"
                                            fontSize="md">
                                            Click to upload{' '}
                                            <span style={{ color: '#666' }}>
                                                or drag and drop
                                            </span>
                                        </Text>
                                        <Text fontSize="sm" color="gray.500">
                                            CSV files only
                                        </Text>
                                    </Box>
                                )}
                            </Box>

                            {/* Instructions */}
                            <div className="mb-3">
                                <Text fontWeight="semibold" fontSize="sm">
                                    Instructions
                                </Text>
                            </div>
                            <UnorderedList>
                                <ListItem>
                                    <Text>
                                        CSV upload is limited to a maximum of{' '}
                                        <strong>{appConfig.csvLimit}</strong>{' '}
                                        items per upload.
                                    </Text>
                                </ListItem>
                                <ListItem>
                                    <Text>
                                        To delete a registration, ensure the
                                        &quot;Delete&quot; field is set to{' '}
                                        <strong>TRUE</strong>
                                    </Text>
                                </ListItem>
                                <ListItem>
                                    <Text>
                                        Please ensure you keep the original CSV
                                        file, in case of data corruption.
                                    </Text>
                                </ListItem>
                                <ListItem>
                                    <Text>
                                        <strong>
                                            You are responsible for the changes
                                            you are about to make through this
                                            data import.
                                        </strong>
                                    </Text>
                                </ListItem>
                            </UnorderedList>
                        </>
                    )}
                </CardBody>

                {/* Navigation Buttons */}
                <CardFooter justifyContent="flex-end" gap={3} mt={4} p={0}>
                    <Button
                        variant="outline"
                        minW="150"
                        bg={darkMode ? 'white' : null}
                        size="sm"
                        _hover={{ opacity: 0.8 }}
                        onClick={() => navigate(-1)}>
                        Back
                    </Button>
                    <Button
                        minW="150"
                        size="sm"
                        onClick={handleValidate}
                        isDisabled={!uploadedFile}>
                        Validate File
                    </Button>
                </CardFooter>
            </Card>
        </PageWrapper>
    );
}
